if (!customElements.get('wls-article-grid')) {
  class WLSArticleGrid extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.abortController = null;
      this.onClick = this._onClick.bind(this);
      this.onPopState = this._onPopState.bind(this);
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.content = this.querySelector('.js-wls-article-grid-content');
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    /**
     * Intercept pagination link clicks and load via Section Rendering API
     * @param {Event} event - The click event
     * @returns {void}
     */
    _onClick(event) {
      const link = event.target.closest('wls-pagination a[data-page]');
      if (!link || !this.sectionId) return;

      event.preventDefault();
      event.stopPropagation();

      this.loadPage(link.dataset.page, { updateHistory: true });
    }

    /**
     * Keep asynchronously loaded content in sync with browser Back/Forward.
     * @returns {void}
     */
    _onPopState() {
      const page = new URL(window.location.href).searchParams.get('page') || '1';
      this.loadPage(page, { updateHistory: false });
    }

    /**
     * Fetch a page of this section and replace its markup without reloading the page
     * @param {string|number} page - Page number to load
     * @param {{updateHistory?: boolean}} options - Whether to add the page to browser history
     * @returns {Promise<void>}
     */
    async loadPage(page, { updateHistory = false } = {}) {
      if (this.isLoading || !this.sectionId || !page) return;

      this.isLoading = true;
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const browserUrl = new URL(window.location.href);
      browserUrl.searchParams.set('page', String(page));

      const fetchUrl = new URL(browserUrl.toString());
      fetchUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(fetchUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('wls-article-grid');

        if (!newGrid) return;

        const newContent = newGrid.querySelector('.js-wls-article-grid-content');
        if (!newContent || !this.content) return;

        this.content.replaceWith(newContent);
        this.content = newContent;

        if (updateHistory) {
          window.history.pushState({ page: String(page) }, '', browserUrl.toString());
        }

        this.keepPaginationInView();
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('WLSArticleGrid: failed to load page', error);
      } finally {
        this.isLoading = false;
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }

    /**
     * Re-center pagination after a page change when it would otherwise leave the
     * viewport (e.g. switching from a tall page to a short trailing page).
     * @returns {void}
     */
    keepPaginationInView() {
      const pagination = this.querySelector('wls-pagination');
      if (!pagination) return;

      const rect = pagination.getBoundingClientRect();
      const comfortableZone = window.innerHeight * 0.2;
      const isComfortablyVisible = rect.top >= comfortableZone && rect.bottom <= window.innerHeight - comfortableZone;

      if (!isComfortablyVisible) {
        pagination.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  customElements.define('wls-article-grid', WLSArticleGrid);
}
